// ipad-pwa/src/lib/idbStorage.js
// =====================================================================
// Tiny IndexedDB key/value wrapper. We use IDB instead of localStorage
// for the pairing/session data because iOS Safari is significantly
// more aggressive about evicting localStorage from PWAs under storage
// pressure or after periods of inactivity, even with
// navigator.storage.persist() granted. IDB is granted stronger
// persistence guarantees in the same conditions.
//
// Single object store ('kv'). Values are passed through structuredClone
// by IDB so they can be any JSON-compatible shape.
// =====================================================================

const DB_NAME = 'iptbc-ipad';
const STORE = 'kv';
let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

export async function idbGet(key) {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('[idb] get failed', key, err);
    return null;
  }
}

export async function idbSet(key, value) {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn('[idb] set failed', key, err);
  }
}

export async function idbDelete(key) {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn('[idb] delete failed', key, err);
  }
}
